/**
 * Central registry of `data-testid` values used across the editor UI.
 *
 * Single source of truth: components render `data-testid={testIds.…}`,
 * and the e2e Page Object Model (`e2e/pages/`) selects by the same
 * constants. When a testid changes it changes here once — and this file
 * doubles as a discoverable map of what the UI exposes to automation.
 *
 * Grouped by UI area. Entries are added as components are wired to the
 * registry; not every existing testid in the app is here yet (that
 * migration is incremental).
 */
export const testIds = {
  /** Title page editing modal: author list, live paper, Edit as LaTeX. */
  titleEdit: {
    modal: "title-edit-modal",
    author: "title-edit-author",
    authorName: "title-edit-author-name",
    authorLine: "title-edit-author-line",
    authorEmail: "title-edit-author-email",
    authorNote: "title-edit-author-note",
    titleNote: "title-edit-title-note",
    noteSymbol: "title-edit-note-symbol",
    addAuthor: "title-edit-add-author",
    addLine: "title-edit-add-line",
    addNote: "title-edit-add-note",
    addTitleNote: "title-edit-add-title-note",
    moveUp: "title-edit-move-up",
    moveDown: "title-edit-move-down",
    remove: "title-edit-remove",
    modeFields: "title-edit-mode-fields",
    modeLatex: "title-edit-mode-latex",
    latex: "title-edit-latex",
    latexStatus: "title-edit-latex-status",
    keptChip: "title-edit-kept-chip",
    keptChipRemove: "title-edit-kept-chip-remove",
    duplicateNote: "title-edit-duplicate-note",
    toggle: "title-edit-toggle",
    more: "title-edit-more",
    preview: "title-edit-preview",
    previewStrip: "title-edit-preview-strip",
    previewShow: "title-edit-preview-show",
  },

  /** Document themes — design-handoffs/2026-10-05-document-themes (1e). */
  look: {
    /** Document settings → Look, the whole section. */
    section: "doc-settings-look",
    /** One theme thumbnail, e.g. `doc-look-theme-index`. */
    theme: (id: string) => `doc-look-theme-${id}`,
    /** The selected theme's "for" line. */
    forLine: "doc-look-for",
    /** Paper: Theme's paper · White always (options carry `data-value`). */
    paper: "doc-look-paper",
    paperOption: "doc-look-paper-option",
    /** Index's colour sequence, as numbered swatches. */
    sequence: "doc-look-sequence",
    /** "Pin a colour to a chapter…" — opens the per-chapter list. */
    pinOpen: "doc-look-pin-open",
    /** One chapter's colour select (+ `data-block-id`). */
    pinColour: "doc-look-pin-colour",
    /** Why themes are off (a publisher class). */
    lockedNote: "doc-look-locked-note",
    /** The schematic thumbnail itself (+ `data-theme`). */
    thumb: "theme-thumb",
    /** Export PDF: "Look: Index ▾", for this export only. */
    exportLook: "export-pdf-look",
    /** Export PDF: Print-safe (white paper; dark grounds take their light pair). */
    exportPrintSafe: "export-pdf-print-safe",
    /** Export PDF: Accessible PDF (tagged PDF/UA-2 for screen readers). */
    exportAccessible: "export-pdf-accessible",
    /** Index: a chapter's number chip in the outline (+ `data-colour`). */
    outlineChip: "outline-chapter-chip",
    /** Index: the canvas's per-heading colour rules. */
    canvasStyle: "canvas-look-style",
    /** The canvas page (+ `data-paper="theme|white"`, `data-paper-mode="true|dark-tint"`). */
    canvasPage: "canvas-page",
    /** The stamp's theme name ("Article · Carnet"): opens the popover at Look. */
    stampLook: "doc-stamp-look",
    /** Look's header in the popover, "Look · Carnet". */
    header: "doc-look-header",
    /** The check badge on the selected theme thumbnail. */
    selectedBadge: "doc-look-selected-badge",
    /** The one-time hint under the stamp: a coloured paper shown darker in Dark. */
    darkPaperHint: "doc-dark-paper-hint",
    darkPaperHintDismiss: "doc-dark-paper-hint-dismiss",
    /** Look → Tables (2f): Ruled · Banded · Header band (options carry `data-value`). */
    tablesStyle: "doc-look-tables-style",
    tablesStyleOption: "doc-look-tables-style-option",
    /** Look → Tables: Normal · Compact. */
    tablesDensity: "doc-look-tables-density",
    tablesDensityOption: "doc-look-tables-density-option",
    /** Look → Tables: captions Above · Below. */
    captionPosition: "doc-look-caption-position",
    captionPositionOption: "doc-look-caption-position-option",
    /** The live sample table under the Tables row (+ `data-style`). */
    tablesSample: "doc-look-tables-sample",
    /** A document card's schematic page (2c, + `data-theme`). */
    docCardThumb: "doc-card-thumb",
    /** A grey pill with the section's number (2b, + `data-section-id`). */
    sectionChip: "section-chip",
  },

  /** The warm accent's uses (Olivia, 1f; REPLY-2026-10-06b). */
  warm: {
    /** A ⌘K result's matched characters, in the title and snippet lines. */
    searchHitMark: "search-hit-mark",
    /** `Badge variant="new"`: one on screen at a time (Look → Tables today). */
    badgeNew: "badge-new",
  },

  /** The sticky kicker on the canvas (document themes, 2d). */
  kicker: {
    /** The pill: chapter tab, chapter name, › current subsection. */
    root: "canvas-kicker",
    /** Its ▾, which opens the outline. */
    toggle: "canvas-kicker-toggle",
    /** The outline popover (⌘⇧O opens it from anywhere). */
    outline: "canvas-kicker-outline",
    /** One heading in it (+ `data-block-id`, `data-current`). */
    outlineItem: "canvas-kicker-outline-item",
  },

  /** Studio block-cards editor — a single block card and its chrome. */
  studioCard: {
    /** The whole Studio shell root — present once the page mounts. */
    shell: "studio-shell",
    /** A Studio chrome tab (Edit / Preview / Validate), e.g. `studio-tab-edit`. */
    tab: (name: string) => `studio-tab-${name}`,
    root: "block-card",
    dragHandle: "block-card-drag-handle",
    editButton: "block-card-edit-button",
    kebab: "block-card-kebab",
    addButton: "block-card-add-button",
    addBefore: "block-card-add-before",
    addAfter: "block-card-add-after",
    typeBadge: "block-card-type-badge",
    menuPreview: "block-card-menu-preview",
    menuLatex: "block-card-menu-latex",
    menuDuplicate: "block-card-menu-duplicate",
    menuDelete: "block-card-menu-delete",
  },

  /** The "Add block" menu (Studio + between-cards inserters). */
  addBlock: {
    button: "add-block-button",
    buttonIcon: "add-block-button-icon",
    /** Per block-type menu item, e.g. `add-block-item-heading`. */
    item: (type: string) => `add-block-item-${type}`,
  },

  /** Mobile Studio chrome. */
  studioMobile: {
    addFab: "studio-mobile-add-fab",
    panelsButton: "studio-mobile-panels-button",
    shareButton: "studio-mobile-share-button",
    validationPip: "studio-mobile-validation-pip",
    previewButton: "studio-mobile-preview-button",
    tabStudio: "studio-mobile-tab-studio",
    tabSettings: "studio-mobile-tab-settings",
  },

  /** App shell — the invariant appbar (shell & documents handoff §11). */
  appBar: {
    root: "appbar",
    navToggle: "appbar-navtoggle",
    switcher: "app-switcher",
    breadcrumb: "breadcrumb",
    cmdk: "cmdk",
    themeToggle: "theme-toggle",
    help: "help",
    account: "account",
  },

  /** The Lilia menu — the switcher with search inside (navigation handoff §2). */
  liliaMenu: {
    root: "lilia-menu",
    scrim: "lilia-menu-scrim",
    search: "lilia-menu-search",
    home: "lilia-menu-home",
    /** A pinned tool's tile, e.g. `lilia-menu-pinned-tables`; + data-current. */
    pinned: (tool: string) => `lilia-menu-pinned-${tool}`,
    allToolsOpen: "lilia-menu-all-tools-open",
    allTools: "lilia-menu-all-tools",
    back: "lilia-menu-back",
    /** A row in All tools; + data-tool. */
    tool: "lilia-menu-tool",
    toolPin: "lilia-menu-tool-pin",
    recent: "lilia-menu-recent",
    /** A recent item; + data-tool, data-id. */
    recentItem: "lilia-menu-recent-item",
    /** While typing: the results, their groups (+ data-group) and rows (+ data-kind, data-id). */
    results: "lilia-menu-results",
    group: "lilia-menu-group",
    result: "lilia-menu-result",
    settings: "lilia-menu-settings",
    trash: "lilia-menu-trash",
    help: "lilia-menu-help",
  },

  /** Home — `/` (navigation handoff §3). */
  home: {
    root: "home",
    continue: "home-continue",
    continueMeta: "home-continue-meta",
    /** + data-tool. */
    toolCard: "home-tool-card",
    toolCardOpen: "home-tool-card-open",
    toolCardCreate: "home-tool-card-create",
    /** + data-id. */
    toolRow: "home-tool-row",
    toolRowWhat: "home-tool-row-what",
    moreTools: "home-more-tools",
    /** + data-tool. */
    moreTool: "home-more-tool",
    firstDay: "home-first-day",
    newPaper: "home-new-paper",
    importPaper: "home-import",
    newTable: "home-new-table",
    /** Shared with you: documents someone else owns (a card, rows + data-doc-id). */
    shared: "home-shared",
    sharedRow: "home-shared-row",
    sharedRowWho: "home-shared-row-who",
    sharedAll: "home-shared-all",
  },

  /** First run 5a (Olivia, 14 Oct; reports/2026-10-21-welcome-onboarding.md): the first screen. */
  firstRun: {
    root: "firstrun",
    blank: "firstrun-blank",
    template: "firstrun-template",
    import: "firstrun-import",
    describe: "firstrun-ask",
    plan: "firstrun-plan",
    dropOverlay: "firstrun-drop",
    /** 3a, the phone: a list of three. */
    mobile: "mobile-firstrun",
    mobileBlank: "mobile-firstrun-blank",
    mobileDescribe: "mobile-firstrun-describe",
    mobileImport: "mobile-firstrun-import",
  },

  /** The tour (5d, Olivia 14 Oct): one card per step, + data-step. */
  tour: {
    step: "tour-step",
    skip: "tour-skip",
    back: "tour-back",
    next: "tour-next",
    takeTour: "help-take-tour",
    /** 2a: an invited person's first arrival. */
    invitedWelcome: "invited-welcome",
    invitedWelcomeClose: "invited-welcome-close",
  },

  /** A tool's own left rail (navigation handoff §1, 27 Sep 2026). */
  /** The public Terms and Privacy pages (launch redesign 2a/2b, Olivia 14 Oct). */
  legal: {
    page: "legal-page",
    /** The contents rail; its links go to `#clause-<n>`. */
    contents: "legal-contents",
    /** The same, as a select on phones. */
    contentsSelect: "legal-contents-select",
    /** Terms / Privacy; + data-doc (terms | privacy). */
    switch: "legal-switch",
    inShort: "legal-in-short",
    draftNotice: "legal-draft-notice",
    print: "legal-print",
  },

  /** Plan surfaces (launch redesign 4–6, Olivia 14 Oct). */
  plan: {
    /** 4b: starting a document or an import with no room left. + data-limit (documents | imports). */
    limitDialog: "limit-dialog",
    limitOpenDocuments: "limit-dialog-open-documents",
    limitDismiss: "limit-dialog-dismiss",
    /** 4f: a line above Ask Lilia's box from 80% of the month's allowance. */
    askAllowanceNote: "ask-allowance-note",
    /** 4f: in place of the box once the allowance is used. */
    askAllowanceUsed: "ask-allowance-used",
    /** 6: "Free · 2 of 3 documents" at the foot of the documents rail. */
    documentsUsage: "documents-plan-usage",
    /** An Upgrade or See plans button, wherever it is: opens 4a. + data-from (limit | rail | settings | ask). */
    seePlans: "plan-see-plans",
    /** 4a: the plans dialog. + data-interval (month | year). */
    plansDialog: "plans-dialog",
    /** 4a: Monthly / Yearly. + data-interval. */
    plansInterval: "plans-interval",
    /** 4a: one column per plan. + data-plan (free | pro | pro_plus …), data-current. */
    plansColumn: "plans-column",
    /** 4a: choose a plan: straight to Stripe Checkout. + data-plan. */
    plansUpgrade: "plans-upgrade",
    /** 4a and Settings → Plan: Stripe's portal, for a paid plan. */
    plansManage: "plans-manage",
    /** 4a: payments aren't open yet (no Stripe keys): nothing was charged. */
    plansUnavailable: "plans-unavailable",
    /** 4c: back from Stripe after paying. + data-state (waiting | done | slow). */
    checkoutSuccess: "checkout-success",
    /** 4c: back from Stripe without paying. */
    checkoutCancelled: "checkout-cancelled",
    checkoutContinue: "checkout-continue",
  },

  toolNav: {
    /** + data-tool (documents | tables | references | settings | …). */
    root: "tool-nav",
    /** A view or a group item; + data-view (mine, has-problems, tag:drafts…). */
    item: "tool-nav-item",
    trash: "tool-nav-trash",
  },

  /** The app-bar crumb `L › Tool › item` (navigation handoff §1, §5). */
  appCrumb: {
    root: "app-crumb",
    tool: "app-crumb-tool",
    /** The editor's way back: the view the paper was opened from. */
    editorOrigin: "editor-origin-crumb",
    /** In the editor, what is open over the paper (a figure's split view). */
    item: "app-crumb-item",
  },

  /** App-level Ask Lilia (context follows the surface, §4). */
  askLilia: {
    panel: "ai-panel",
    context: "ai-context",
    close: "ai-close",
    reopen: "ai-reopen",
    modeToggle: "ai-mode-toggle",
    input: "ai-input",
    send: "ai-send",
    chip: "ai-chip",
    ref: "ai-ref",
    thinking: "ai-thinking",
  },

  /**
   * Chat-First editor shell (handoff 2026-07-15) — Primary (chat + artifact)
   * vs Side (doc primary + docked Ask Lilia). Registered centrally so e2e
   * and the prototype testid map stay in sync.
   */
  chatFirst: {
    layoutSwitch: "layout-switch",
    layoutPrimary: "layout-primary",
    layoutSide: "layout-side",
    artifactPane: "artifact-pane",
    chatPrimaryColumn: "chat-primary-column",
    artifactClose: "artifact-close",
    artifactReopen: "artifact-reopen",
    artifactChip: "artifact-chip",
    /** Primary artifact surfaces: Flow · Block · Split · Source. */
    workspaceGrid: "workspace-grid",
    editorColumn: "editor-column",
    /** Both panes carry the same strip; the side is a data attribute on it. */
    leftPaneToggle: "left-pane-toggle",
    paneHeader: "pane-header",
    documentSurfaceTabs: "document-surface-tabs",
    paneTabAsk: "pane-tab-ask",
    paneTabEdit: "pane-tab-edit",
    paneTabPdf: "pane-tab-pdf",
    paneTabSource: "pane-tab-source",
    /** In-card paired LaTeX when Block surface is active. */
    artifactPairedSource: "artifact-paired-source",
    pinnedBadge: "pinned-badge",
    // The surface-specific strip left of the pane tabs. Empty for Edit, PDF
    // and Source; on Ask Lilia it holds the thread switcher below.
    paneContext: "pane-context",
    // Chat surface header (design handoff 2026-09-11, §9). The title IS the
    // switcher: one click lists threads, a second renames in place.
    paneContextSwitcher: "pane-context-switcher",
    paneContextTitle: "pane-context-title",
    chatTitleInput: "chat-title-input",
    chatNew: "chat-new",
    /** Artifact Block/Flow/… segment (design: mode-toggle). */
    modeToggle: "mode-toggle",
    /** Thin top indeterminate bar while document / mode settles. */
    workspaceLoadingBar: "workspace-loading-bar",
    resizeHandle: "resize-handle",
    sideAiPanel: "side-ai-panel",
    sideAiClose: "side-ai-close",
    sideAiReopen: "side-ai-reopen",
    composer: "composer",
    moveToSide: "move-to-side",
  },

  /** My Documents surface (§5). */
  /**
   * Editor chrome — the bar above the panes. Design handoff 2026-09-11:
   * one save indicator (not a Save button plus a sync dot), and a formatting
   * rail split by scope rather than by two words that meant the same thing.
   */
  /** RefreshButton — one component for every refresh (saved-moment §5). The
   *  group is `refresh-button` with data-state (idle · working · done ·
   *  failed); the button inside keeps each place's own testid, or `trigger`. */
  refreshButton: {
    root: "refresh-button",
    trigger: "refresh-button-trigger",
    retry: "refresh-button-retry",
    timestamp: "refresh-button-timestamp",
  },

  editor: {
    /** The save indicator's pill — the card's trigger, with `data-state`
     *  (saved · saving · offline · failed · version · new). Kept from the
     *  indicator it replaced, so every spec that checks a save still does. */
    docSaveState: "doc-save-state",
    /** The saved moment (handoff 2026-09-27): the group around the pill, with
     *  `data-state`, `data-moment="full|tick"` (the last one played) and
     *  `data-full-count` / `data-tick-count`. */
    saveIndicator: "save-indicator",
    saveIndicatorRetry: "save-indicator-retry",
    saveIndicatorCard: "save-indicator-card",
    saveIndicatorSaveVersion: "save-indicator-save-version",
    saveIndicatorVersionName: "save-indicator-version-name",
    saveIndicatorVersionSubmit: "save-indicator-version-submit",
    saveIndicatorFun: "save-indicator-fun",
    /** Fun · the safe: the dial's face, warm only while it turns (timed in saveIndicator.css). */
    saveIndicatorSafeDial: "save-indicator-safe-dial",
    /** Style: Pro | Fun, in the card's footer and in Settings → Appearance. */
    personalityToggle: "personality-toggle",
    personalityOption: "personality-option",
    /** History, in the indicator's card. */
    docVersionHistory: "doc-version-history",
    /** `¶ Body` — everything scoped to the current BLOCK. */
    fmtBlock: "fmt-block",
    /** `Aa Text` — everything scoped to the current SELECTION. */
    fmtText: "fmt-text",
    /** Export, promoted to the only filled control in the bar. */
    docExport: "doc-export",
    docExportMenu: "doc-export-menu",
    docExportWord: "doc-export-format-docx",
    docExportSettings: "doc-export-settings",
    /** The ⋮ that receives what the deleted second row was carrying. */
    docActionsOverflow: "doc-actions-overflow",
    /** The editor ⋯'s document actions (documents-actions handoff §2). */
    moreSettings: "editor-more-settings",
    moreRename: "editor-more-rename",
    moreDuplicate: "editor-more-duplicate",
    /** Save as template… — opens the Save as template dialog (templates handoff §2). */
    moreSaveTemplate: "editor-more-save-template",
    moreTrash: "editor-more-trash",
    moreRemove: "editor-more-remove",
    fmtInsert: "fmt-insert",
    /** Theme picker accent options — repeating, disambiguated by
     *  data-accent-name (cerulean | plum | ink). */
    accentOption: "accent-option",
    accentOptions: "accent-options",
  },

  /**
   * Templates — the gallery at /templates and the one Save as template
   * form it shares with the editor's ⋯ (templates handoff, 27 Sep 2026).
   */
  templates: {
    create: "templates-create",
    empty: "templates-empty",
    emptyCreate: "templates-empty-create",
    /** The Save as template dialog: Name · Category · Visibility. */
    saveDialog: "save-template-dialog",
    saveName: "save-template-name",
    saveCategory: "save-template-category",
    /** Repeating; disambiguated by data-value. */
    saveCategoryOption: "save-template-category-option",
    saveVisibility: "save-template-visibility",
    /** Repeating; data-value is private | public. */
    saveVisibilityOption: "save-template-visibility-option",
    saveCancel: "save-template-cancel",
    saveSubmit: "save-template-submit",
    saveError: "save-template-error",
    /** The gallery's Create template → pick one of your documents. */
    pickDialog: "template-pick-dialog",
    pickSearch: "template-pick-search",
    /** Repeating; disambiguated by data-doc-id. */
    pickItem: "template-pick-item",
    pickEmpty: "template-pick-empty",
    pickCancel: "template-pick-cancel",
  },

  documents: {
    /* The sidebar's nav-link-* / nav-tag / nav-account went with DocsSidebar;
       the Documents rail is a ToolNav (see toolNav). */
    search: "doc-search",
    /** Filter chip, e.g. `doc-filter-all`. */
    filter: (f: string) => `doc-filter-${f}`,
    sort: "doc-sort",
    /** The filtered-empty panel's Clear filters. */
    clearFilters: "doc-clear-filters",
    /** View-mode segment, e.g. `view-switch-cards`. */
    view: (v: string) => `view-switch-${v}`,
    new: "doc-new",
    /** Attention group, e.g. `doc-group-attn`. */
    group: (g: string) => `doc-group-${g}`,
    /** "Jump back in" resume strip + its wide cards. */
    resume: "doc-resume",
    resumeCard: "doc-resume-card",
    resumeMeta: "doc-resume-meta",
    card: "doc-card",
    /** Card parts (documents-page handoff 2026-09-14 §6). */
    cardTitle: "doc-card-title",
    cardMeta: "doc-card-meta",
    cardStatus: "doc-card-status",
    cardNext: "doc-card-next",
    cardOverflow: "doc-card-overflow",
    /** The card's ⋯ menu and its items (documents-actions handoff, 26 Sep). */
    cardMenu: "doc-card-menu",
    cardMenuRename: "doc-card-menu-rename",
    cardMenuDuplicate: "doc-card-menu-duplicate",
    cardMenuTrash: "doc-card-menu-trash",
    cardMenuRemove: "doc-card-menu-remove",
    cardTitleInput: "doc-card-title-input",
    empty: "doc-empty",
    loading: "doc-loading",
    noMatch: "doc-no-match",
  },

  /** The LaTeX view's Preamble · Code · Blocks (DESIGN-14). */
  latexView: {
    tabs: "latex-view-tabs",
    /** One of the three (+ `data-tab` preamble|code|blocks, `aria-selected`). */
    tab: "latex-view-tab",
    blocks: "latex-blocks",
    /** A block's card (+ `data-block-id`, `data-block-type`, `data-selected`, `data-issue`). */
    blockCard: "latex-block-card",
    blockRendered: "latex-block-rendered",
    blockLines: "latex-block-lines",
    blockEdit: "latex-block-edit",
    blockCopy: "latex-block-copy",
    /** Phone: a long table's "Show all N rows". */
    blockTableFold: "latex-block-table-fold",
    /** Phone: "Plain text: no LaTeX commands in this paragraph." */
    blockPlain: "latex-block-plain",
    /** Phone: Code opened from a card, "From … · lines a–b" with Back to block. */
    codeContext: "latex-code-context",
    backToBlock: "latex-back-to-block",
    /** Phone: a Preamble group's fold row. */
    preambleGroupToggle: "latex-preamble-group-toggle",
    blockFold: "latex-block-fold",
    /** The band between rendered part and code (+ `data-severity`). */
    blockIssue: "latex-block-issue",
    issuesChip: "latex-issues-chip",
    preamble: "latex-preamble",
    /** A group (+ `data-group`: encoding, maths, …, definitions, info, yours). */
    preambleGroup: "latex-preamble-group",
    /** A row (+ `data-yours` on the author's own lines). */
    preambleRow: "latex-preamble-row",
    preambleFold: "latex-preamble-row-fold",
    editPreamble: "latex-preamble-edit",
  },

  /** Sign-in and sign-up (launch redesign 1a–1g). */
  /** "Publish with…" in the document stamp (publisher templates, 25 Oct 2026). */
  publishWith: {
    section: "publish-with",
    /** The venue + stage select; "" = Lilia's own layout. */
    select: "publish-with-select",
    /** What the chosen profile sets and requires: + data-profile. */
    facts: "publish-with-facts",
    guidelines: "publish-with-guidelines",
    /** Review | Camera-ready, when the venue has both: a button per stage, data-stage, aria-pressed. */
    stage: "publish-with-stage",
    /** The class version installed on the server. */
    installed: "publish-with-installed",
    /** A co-author's note: the venue is the owner's to choose. */
    ownerOnly: "publish-with-owner-only",
  },
  auth: {
    /** Sign-up's "what Free gives" card (1a). */
    freePromise: "signup-free-promise",
    /** The invitation a visitor arrived with (1c): + `data-role` edit|comment|view. */
    context: "auth-context",
    /** The terms line under the create button (1a): replaced the checkbox. */
    terms: "signup-terms",
    signUpName: "signup-name",
    signUpEmail: "signup-email",
    signUpPassword: "signup-password",
    signUpSubmit: "signup-submit",
    /** Phones (1g): the first step's Continue, before name and password. */
    signUpContinue: "signup-continue",
    signInError: "signin-error",
    /** "Email me a link instead" under the password (23 Oct: asks the API). */
    signInMagicLink: "signin-magic-link",
    forgotEmail: "forgot-password-email",
    forgotSubmit: "forgot-password-submit",
    /** The "sent" state's Resend and Use a different email. */
    forgotResend: "forgot-password-resend",
    forgotDifferentEmail: "forgot-password-different-email",
    /** /reset-password's error banner: + data-kind failed | weak | stale | noToken. */
    resetError: "reset-password-error",
    /** /reset-password's second field, and its "don't match" message. */
    resetConfirm: "reset-password-confirm",
    resetMismatch: "reset-password-mismatch",
    /** /mfa-enrollment, two-step set-up with an authenticator app. */
    mfaEnroll: {
      qr: "mfa-enroll-qr",
      setupKey: "mfa-enroll-setup-key",
      copyKey: "mfa-enroll-copy-key",
      code: "mfa-enroll-code",
      submit: "mfa-enroll-submit",
      skip: "mfa-enroll-skip",
      /** Stytch refused to create the authenticator: the message and Try again. */
      startFailed: "mfa-enroll-start-failed",
      retry: "mfa-enroll-retry",
      codes: "mfa-enroll-codes",
      download: "mfa-enroll-download",
      copyAll: "mfa-enroll-copy-all",
      saved: "mfa-enroll-saved",
      finish: "mfa-enroll-finish",
    },
  },

  /** Sharing surface (§ Sharing & Settings) — /shared, 3 tabs. */
  /** The Trash page. Rows and their buttons repeat, with data-doc-id. */
  trash: {
    row: "trash-row",
    select: "trash-select",
    restore: "trash-restore",
    deleteForever: "trash-delete-forever",
    /** All tools · Documents · Tables; + data-tool (all | documents | tables). */
    toolFilter: "trash-tool-filter",
    /** A tool's group of trashed items; + data-tool. */
    section: "trash-section",
  },

  /** Documents · For publishers: the compliance dashboard (25 Oct 2026). */
  publishing: {
    surface: "publishing-surface",
    /** One document: + data-doc-id, data-status (passed | warnings | errors). */
    row: "publishing-row",
    open: "publishing-open",
    check: "publishing-check",
    empty: "publishing-empty",
    templates: "publishing-templates",
  },
  sharing: {
    surface: "sharing-surface",
    /** Tab button; disambiguate via `data-tab={with|by|people}`. */
    tab: "sharing-tab",
    /** Shared-with-me card (+ `data-doc-id`). */
    card: "share-card",
    cardRole: "share-card-role",
    cardFrom: "share-card-from",
    cardOpen: "share-card-open",
    /** Shared-by-me table row (+ `data-doc-id`). */
    row: "share-row",
    rowMenu: "share-row-menu",
    people: "share-people",
    linkStatus: "share-link-status",
    linkExpiry: "share-link-expiry",
    manage: "share-manage",
    /** An invitation not yet accepted (Olivia, 10 Oct, §c): `data-invite-id`, `data-status` pending|expired. */
    pendingInvite: "share-pending-invite",
    pendingResend: "share-pending-resend",
    pendingCopyLink: "share-pending-copy-link",
    pendingCancel: "share-pending-cancel",
    rowOpen: "share-row-open",
    byMeSearch: "share-by-me-search",
    /** People tab row (+ `data-person-key`, `data-status` active|pending). */
    personRow: "people-row",
    personToggle: "people-row-toggle",
    /** One of a person's documents in the expanded row (+ `data-doc-id`). */
    personDoc: "people-row-doc",
    personRole: "people-row-role",
    /** A role in that picker (+ `data-role`). */
    personRoleOption: "people-row-role-option",
    personResend: "people-row-resend",
    personRemove: "people-row-remove",
    peopleSearch: "people-search",
    /** "4 people · 1 pending" under the people list's heading. */
    peopleSummary: "share-people-summary",
    /** Remove someone (Olivia, 10 Oct): the confirmation and its two boxes. */
    removeDialog: {
      root: "remove-access-dialog",
      notify: "remove-access-notify",
      allowReply: "remove-access-allow-reply",
      summary: "remove-access-summary",
      cancel: "remove-access-cancel",
      confirm: "remove-access-confirm",
    },
    /** People tab — invite row. */
    inviteInput: "share-invite-input",
    inviteRole: "share-invite-role",
    inviteSend: "share-invite-send",
    personStatus: "person-status",
    role: "share-role",
    resend: "share-resend",
    revoke: "share-revoke",
    empty: "sharing-empty",
    loading: "sharing-loading",
    noMatch: "sharing-no-match",
  },

  /** Settings surface (§ Sharing & Settings) — /settings, sub-nav + panel. */
  /** /connect/import/:token — the connector's drop page (MCP start_import). */
  connectImport: {
    root: "connect-import",
    drop: "connect-import-drop",
    file: "connect-import-file",
    done: "connect-import-done",
    open: "connect-import-open",
    problem: "connect-import-problem",
    retry: "connect-import-retry",
  },
  /** /oauth/authorize — an outside app (MCP client) asks to use the account. */
  /** The notice that the Terms changed (launch redesign 2c). */
  legalNotice: {
    root: "legal-update-notice",
    see: "legal-update-see",
    ok: "legal-update-ok",
  },
  oauthConsent: {
    root: "oauth-consent",
    abilities: "oauth-consent-abilities",
    allow: "oauth-consent-allow",
    cancel: "oauth-consent-cancel",
    allowEdits: "oauth-consent-allow-edits",
    problem: "oauth-consent-problem",
  },
  helpPage: {
    root: "help-page",
    article: "help-article",
    level: "help-level",
    steps: "help-steps",
    meaning: "help-meaning",
    forLatex: "help-for-latex",
    fallbackNote: "help-fallback-note",
    indexLink: "help-index-link",
    /** Signed out: the plain bar with Sign in. */
    publicBar: "help-public-bar",
    signIn: "help-sign-in",
  },
  /** A closed account (asked to delete): the restore screen when signed in, the page when signed out. */
  accountClosed: {
    gate: "account-closed-gate",
    restore: "account-closed-restore",
    signOut: "account-closed-sign-out",
    page: "account-closed-page",
  },

  settings: {
    surface: "settings-surface",
    /** Profile's save bar: appears when something has changed (launch redesign 3a). */
    savebar: "settings-savebar",
    savebarSave: "settings-savebar-save",
    savebarDiscard: "settings-savebar-discard",
    profileName: "settings-profile-name",
    passwordCurrent: "settings-password-current",
    passwordNew: "settings-password-new",
    passwordChange: "settings-password-change",
    /** Two-step sign-in (3b): the row, data-state on | off | unavailable. */
    twoStep: "settings-two-step",
    twoStepTurnOn: "settings-two-step-on",
    twoStepTurnOff: "settings-two-step-off",
    twoStepShowCodes: "settings-two-step-codes",
    twoStepCodesList: "settings-two-step-codes-list",
    twoStepOffDialog: "settings-two-step-off-dialog",
    twoStepOffCode: "settings-two-step-off-code",
    twoStepOffConfirm: "settings-two-step-off-confirm",
    /** 25 Oct: two-step on or off with a code by email; + data-turn on|off on the dialog. */
    twoStepTurnOnEmail: "settings-two-step-on-email",
    twoStepEmailDialog: "settings-two-step-email-dialog",
    twoStepEmailSend: "settings-two-step-email-send",
    twoStepEmailCode: "settings-two-step-email-code",
    twoStepEmailConfirm: "settings-two-step-email-confirm",
    twoStepEmailClose: "settings-two-step-email-close",
    /** Trusted devices: rows + data-device-id (and data-current on this browser's). */
    trustedDevices: "settings-trusted-devices",
    trustedDevice: "settings-trusted-device",
    trustedDeviceForget: "settings-trusted-device-forget",
    trustedDevicesForgetAll: "settings-trusted-devices-forget-all",
    /** Signed-in devices: the list, one row per session (+ data-current), Sign out, and the rest. */
    sessions: "settings-sessions",
    sessionRow: "settings-session",
    sessionSignOut: "settings-session-sign-out",
    sessionsSignOutOthers: "settings-sessions-sign-out-others",
    /** Plan (3c/3d): the plan card and its meters, data-used/data-limit. */
    planCard: "settings-plan-card",
    usageDocuments: "usage-documents",
    usageImports: "usage-imports",
    usageAsk: "usage-ask",
    /** Connected apps (3e): one row per app, + data-app-id. */
    connectedAppRow: "connected-app-row",
    connectedAppDisconnect: "connected-app-disconnect",
    connectedAppsEmpty: "connected-apps-empty",
    /** Your data (3f/3g). data-status on the export: none | PENDING | PROCESSING | COMPLETED | FAILED. */
    dataExportStatus: "data-export-status",
    dataExportStart: "data-export-start",
    dataExportDownload: "data-export-download",
    deleteAccountOpen: "delete-account-open",
    deleteAccountDialog: "delete-account-dialog",
    deleteAccountEmail: "delete-account-email",
    deleteAccountPassword: "delete-account-password",
    deleteAccountConfirm: "delete-account-confirm",
    deleteAccountCancel: "delete-account-cancel",
    /** Sub-nav link; disambiguate via `data-section`. */
    nav: "settings-nav",
    panel: "settings-panel",
    /** A field row; disambiguate via `data-field`. */
    field: "settings-field",
    /** Theme option; disambiguate via `data-theme`. */
    theme: "settings-theme",
    /** Accent choice in Settings → Appearance; data-accent = cerulean | plum | ink. */
    accent: "settings-accent",
    /** Light style to try (lib/lightStyle): a select. */
    lightStyle: "settings-light-style",
  },

  /** Lilia Convert — import review focused surface. */
  review: {
    surface: "review-surface",
    /** Left panel-rail tab; disambiguate via `data-panel`. */
    railTab: "review-rail-tab",
    panel: "review-panel",
    /** Exceptions ⇄ Show all switch; disambiguate via `data-mode`. */
    modeToggle: "review-mode-toggle",
    /** A block card in the center; + `data-block-id` / `data-block-type`. */
    block: "review-block",
    accept: "review-block-accept",
    reject: "review-block-reject",
    edit: "review-block-edit",
    undo: "review-block-undo",
    clearExceptions: "review-clear-exceptions",
    autonomy: "review-autonomy",
    finish: "review-finish",
    back: "review-back",
    empty: "review-empty",
    loading: "review-loading",
  },
  /**
   * The table tool — a surface a pane becomes when a table block is selected
   * (SPEC.md §1, "8c"). The same interior is exercised standalone at
   * /dev/table-tool, which is a harness rather than a surface anyone is shown,
   * so both mounts select on these.
   *
   * SPEC.md §13 asks for these here rather than inline: they are the contract
   * the e2e suite selects on, and renaming one inline silently breaks specs.
   */
  /**
   * The `@` picker — one trigger over this document's labels and the library.
   * Rows share one testid and carry `data-ref-key` / `data-ref-group`: the
   * repo rule for repeating elements. Olivia's spec wrote `ref-picker-row-<key>`;
   * this is the same contract in the form the e2e suite already expects.
   */
  /** A block's label, derived from its caption. (SPEC-references §4.) */
  /**
   * The References panel (SPEC-references §6). Repeating rows share one testid
   * and carry data-ref-key / data-problem-kind, per the repo rule.
   */
  references: {
    panel: "references-panel",
    problem: "references-problem",
    target: "references-target",
    unusedGroup: "references-unused-group",
    unusedToggle: "references-unused-toggle",
    numberedAt: "references-numbered-at",
    useHere: "references-use-here",
    /** A reference in the prose; `data-ref-state` is numbered | pending | dangling | unresolved. */
    inline: "inline-reference",
  },

  labels: {
    chip: "label-chip",
    chipEdit: "label-chip-edit",
    collision: "label-collision",
    collisionAccept: "label-collision-accept",
  },

  /**
   * Paste-to-fix (SPEC-symbols-equations §6, revised 25 Sep). One chip per
   * paste. `lookUp` repeats, one per symbol, with `data-char`.
   */
  pasteFix: {
    chip: "paste-fix-chip",
    lookUp: "paste-fix-lookup",
    dismiss: "paste-fix-dismiss",
    noSymbol: "paste-fix-no-symbol",
    /** The failing characters, listed inline — the copy depends on them. */
    chars: "paste-fix-chars",
    /** Each underlined character in the document. */
    underlined: "wont-compile-char",
  },
  /** The Symbols panel's honest empty state (SPEC-symbols-equations §7). */
  symbols: {
    search: "symbols-search",
    empty: "symbols-empty",
    /** One per symbol; `data-latex` carries what it inserts. */
    button: "symbol-button",
    /** `data-tab` names the category. */
    tab: "symbols-tab",
  },
  /** The Flow equation block and its edit modal. */
  equation: {
    /** `data-numbered` is "false" for a starred equation. */
    block: "equation-block",
    preview: "equation-preview",
    number: "equation-number",
    mode: "equation-edit-mode",
    numbered: "equation-edit-numbered",
    label: "equation-edit-label",
  },
  /** The Flow code block and its edit modal. */
  code: {
    block: "code-block",
    /** The clickable preview; opens the edit modal. */
    preview: "code-block-preview",
    /** The code editor inside the modal. */
    editSource: "code-edit-source",
  },
  /** The Flow theorem-like block (theorem, lemma, proof, …) and its edit modal. */
  theorem: {
    block: "theorem-block",
    preview: "theorem-preview",
    editText: "theorem-edit-text",
  },
  /** The Flow figure block and its details modal. */
  figure: {
    block: "figure-block",
    /** The caption under the image, typed into in place. */
    caption: "figure-caption",
    edit: "figure-edit",
    editCaption: "figure-edit-caption",
    /** An image figure on the paper panel (Dark, a background that may be transparent). */
    paperPanel: "figure-paper-panel",
  },
  /**
   * A figure whose content is TikZ source (TikZ figures, step 1 —
   * design-handoffs/2026-10-07-tikz-figures). The root carries data-state
   * (drawn | drawing | first | error | empty), data-view (drawing | source)
   * and data-bare for a picture with no caption.
   */
  figureTikz: {
    root: "figure-tikz",
    /** The hover / selection bar: TikZ · Source · ⋯ (data-drawing while it recompiles). */
    bar: "figure-tikz-bar",
    sourceToggle: "figure-tikz-source-toggle",
    more: "figure-tikz-more",
    editSource: "figure-tikz-edit-source",
    makeNumbered: "figure-tikz-make-numbered",
    /** The drawing itself (an <img> of the SVG). */
    drawing: "figure-tikz-drawing",
    /** The first compile's dashed box (data-slow once the note shows). */
    firstLoad: "figure-tikz-first-load",
    /** The read-only code view; each line carries data-line, the failing one data-highlighted. */
    code: "figure-tikz-code",
    codeCopy: "figure-tikz-code-copy",
    error: "figure-tikz-error",
    showSource: "figure-tikz-show-source",
    copyError: "figure-tikz-copy-error",
    tryAgain: "figure-tikz-try-again",
    lastGood: "figure-tikz-last-good",
    /** Source → Edit: the code view's way into the split view. */
    codeEdit: "figure-tikz-code-edit",
    /** ⋯ → Figure details…: placement and alt text, in the edit drawer. */
    details: "figure-tikz-details",
    /** ⋯ → Ask Lilia: Change… / Explain this figure (1c). */
    askChange: "figure-tikz-ask-change",
    askExplain: "figure-tikz-ask-explain",
  },
  /**
   * Ask Lilia on a figure (TikZ step 3, 1c): the change in the split view,
   * and the dock's figure context, suggestions and proposals.
   */
  tikzAsk: {
    /** Over the source while a change is pending (data-drew = true | false). */
    diffBar: "tikz-diff-bar",
    diffKeep: "tikz-diff-keep",
    diffDiscard: "tikz-diff-discard",
    /** The source as a diff; each row data-op = kept | removed | added, data-line. */
    diffSource: "tikz-diff-source",
    /** Before · After on the drawing (data-showing = before | after). */
    diffBeforeAfter: "tikz-diff-before-after",
    diffBefore: "tikz-diff-before",
    diffAfter: "tikz-diff-after",
    /** A proposal in the dock (data-kind = draw | failed | change | caption). */
    aiProposal: "tikz-ai-proposal",
    proposalInsert: "tikz-ai-insert",
    proposalOpenSplit: "tikz-ai-open-split",
    proposalShowChange: "tikz-ai-show-change",
    proposalUseCaption: "tikz-ai-use-caption",
    /** An Explain line chip (data-from, data-to): hover highlights, click scrolls. */
    aiLineChip: "tikz-ai-line-chip",
    /** The dock's context chip ("Figure 3 · TikZ", "New figure · §3.2") and its ×. */
    contextChip: "ask-lilia-figure-context",
    contextClear: "ask-lilia-figure-context-clear",
    /** A suggestion for the figure (data-intent). */
    suggestion: "ask-lilia-figure-suggestion",
  },
  /**
   * The TikZ split view (TikZ step 3, design-handoffs/2026-10-07-tikz-figures
   * 1a, 1b, 1d, 1e). The root carries data-layout (side | stacked | tabs).
   */
  /**
   * Plot this table (TikZ step 4a, design-handoffs/2026-10-07-tikz-figures/
   * README-step4.md): the dialog, its settings panel (also the split view's
   * Plot tab), the linked figure's chip, and detaching.
   */
  plot: {
    /** The table's "Plot this table" (data-disabled when it has no number columns). */
    tableMenuPlot: "table-menu-plot",
    /** Why it can't plot: read to screen readers, shown on a tap. */
    tableMenuPlotReason: "table-menu-plot-reason",
    dialog: "plot-dialog",
    settings: "plot-settings",
    /** Line · Scatter · Bar (data-value; disabled for text x). */
    type: "plot-type",
    x: "plot-x",
    /** An x column chip (data-column, aria-pressed). */
    xOption: "plot-x-option",
    series: "plot-series",
    /** A series row (data-column, data-checked, data-disabled). */
    seriesOption: "plot-series-option",
    axisLabelX: "plot-axis-label-x",
    axisLabelY: "plot-axis-label-y",
    legend: "plot-legend",
    preview: "plot-preview",
    /** data-state = drawing | drawn | error. */
    previewStatus: "plot-preview-status",
    insert: "plot-insert",
    cancel: "plot-cancel",
    /** "Plot · Table 2" on the figure bar (data-state = linked | removed | problem). */
    linkChip: "plot-link-chip",
    settingsOpen: "plot-settings-open",
    /** The Source tab's bar on a linked plot, and its Detach and edit. */
    detachBar: "plot-detach-bar",
    detachEdit: "plot-detach-edit",
    /** "Stop following Table 2?" and its buttons. */
    detachConfirm: "plot-detach-confirm",
    detachConfirmDetach: "plot-detach-confirm-detach",
    detachConfirmKeep: "plot-detach-confirm-keep",
    /** "ViT-B/16 was removed from Table 2, so it's no longer plotted." */
    droppedNotice: "plot-dropped-notice",
  },
  /** The split view's left pane tabs (step 4): Plot · Grid · Source. */
  tikzViews: {
    tabs: "tikz-view-tabs",
    plot: "tikz-view-plot",
    grid: "tikz-view-grid",
    source: "tikz-view-source",
  },
  /**
   * The mobile reader (phones under 768 px; Olivia, design-handoffs/
   * 2026-10-10-mobile-reader). The document is read-only there.
   */
  mobile: {
    /** Publisher templates on the phone (25 Oct 2026): the settings group, the venue lock note, the check sheet's section and findings, a row's venue badge (data-status). */
    settingsPublisher: "mobile-settings-publisher",
    settingsVenueLock: "mobile-settings-venue-lock",
    checkPublisher: "mobile-check-publisher",
    checkPublisherFinding: "mobile-check-publisher-finding",
    venueBadge: "mobile-venue-badge",
    docs: "mobile-docs",
    docsSearch: "mobile-docs-search",
    docsList: "mobile-docs-list",
    docRow: "mobile-doc-row",
    /** Shared with you, first on the list (rows + data-doc-id). */
    sharedGroup: "mobile-shared-group",
    sharedRow: "mobile-shared-row",
    viewOnlyBadge: "mobile-viewonly-badge",
    read: "mobile-read",
    readTopbar: "mobile-read-topbar",
    readBack: "mobile-read-back",
    readOutline: "mobile-read-outline",
    readPreview: "mobile-read-preview",
    readMore: "mobile-read-more",
    readBody: "mobile-read-body",
    /** The selected block's outline (data-block-id). */
    blockSelected: "mobile-block-selected",
    /** "Ask about this paragraph" above the Ask bar. */
    askChip: "mobile-ask-chip",
    askChipClear: "mobile-ask-chip-clear",
    refCard: "mobile-ref-card",
    refGoto: "mobile-ref-goto",
    refCopy: "mobile-ref-copy",
    outlineSheet: "mobile-outline-sheet",
    outlineItem: "mobile-outline-item",
    askBar: "mobile-ask-bar",
    askSheet: "mobile-ask-sheet",
    askSheetClose: "mobile-ask-sheet-close",
    /** The sheet's own New chat (the panel's header is hidden on phones). */
    askNewChat: "mobile-ask-new-chat",
    askInput: "mobile-ask-input",
    askContext: "mobile-ask-context",
    askReadonlyReply: "mobile-ask-readonly-reply",
    changesCard: "mobile-changes-card",
    changesPrev: "mobile-changes-prev",
    changesNext: "mobile-changes-next",
    changesUndoAll: "mobile-changes-undo-all",
    changesKeep: "mobile-changes-keep",
    /** The card's headline: opens the current change's before/after. */
    changesShow: "mobile-changes-show",
    changeMark: "mobile-change-mark",
    changeDiff: "mobile-change-diff",
    changeUndo: "mobile-change-undo",
    /** A removed block's text, to keep it (it comes back only with Undo all). */
    changeCopy: "mobile-change-copy",
    changeNext: "mobile-change-next",
    viewer: "mobile-viewer",
    viewerClose: "mobile-viewer-close",
    viewerAsk: "mobile-viewer-ask",
    preview: "mobile-preview",
    previewClose: "mobile-preview-close",
    previewPage: "mobile-preview-page",
    previewStatus: "mobile-preview-status",
    moreMenu: "mobile-read-more-menu",
    copyLink: "mobile-read-copy-link",
    /** ⋯ → All documents (the documents list). */
    allDocs: "mobile-read-all-docs",
    /** ⋯ is a sheet: Share… · Check document · Document details · All documents. */
    moreSheet: "mobile-more-sheet",
    moreShare: "mobile-more-share",
    moreCheck: "mobile-more-check",
    moreDetails: "mobile-more-details",
    /** A red dot on ⋯ while the document has errors. */
    moreDot: "mobile-more-dot",
    /** The avatar on top-level phone screens and its account sheet (Olivia, 11 Oct): Appearance, Sign out. */
    accountAvatar: "mobile-account-avatar",
    accountSheet: "mobile-account-sheet",
    /** Settings in the account sheet, and phone Settings (launch redesign 3h): the list, one row per
     *  section (+ data-section), the back arrow, and the Plan screen. */
    settingsOpen: "mobile-settings-open",
    settings: "mobile-settings",
    settingsItem: "mobile-settings-item",
    settingsBack: "mobile-settings-back",
    settingsPlan: "mobile-settings-plan",
    /** Language in the account sheet (en, fr, es). */
    language: "mobile-language",
    signOut: "mobile-sign-out",
    shareSheet: "mobile-share-sheet",
    shareCopyLink: "mobile-share-copy-link",
    sharePeople: "mobile-share-people",
    /** The owner's role picker on a person (+ data-user-id): Can edit · Can view · Remove access. */
    shareRole: "mobile-share-role",
    /** Invite by email on a phone (the owner): the form, its field, role and send. */
    shareInvite: "mobile-share-invite",
    shareInviteEmail: "mobile-share-invite-email",
    shareInviteRole: "mobile-share-invite-role",
    shareInviteSend: "mobile-share-invite-send",
    /** "Add Lilia to your home screen", after the documents list; its dismiss. */
    homeHint: "mobile-home-hint",
    homeHintDismiss: "mobile-home-hint-dismiss",
    /** A new document on a phone (Olivia, 10 Oct): the Documents +, the page and its parts. */
    docsNew: "mobile-docs-new",
    newDoc: "mobile-new-doc",
    /** A choice; + data-setting (type | paper | language), data-value, aria-checked. */
    newDocOption: "mobile-new-doc-option",
    newDocLook: "mobile-new-doc-look",
    newDocName: "mobile-new-doc-name",
    newDocCreate: "mobile-new-doc-create",
    /** A starter chip in Ask Lilia after Create. */
    askStarter: "mobile-ask-starter",
    /** ⋯ → Look… and Page & settings… (Olivia, 10 Oct). */
    moreLook: "mobile-more-look",
    moreSettings: "mobile-more-settings",
    /** ⋯ → Appearance; its choices are `${appearance}-system|light|dark`. */
    appearance: "mobile-appearance",
    /** The light style being tried, under Appearance (a native select). */
    lightStyle: "mobile-light-style",
    lookSheet: "mobile-look-sheet",
    /** A theme card; + data-theme, aria-checked. */
    lookCard: "mobile-look-card",
    lookApply: "mobile-look-apply",
    lookCancel: "mobile-look-cancel",
    lookClose: "mobile-look-close",
    settingsSheet: "mobile-settings-sheet",
    /** The title field: the one text input on a phone. */
    settingsTitle: "mobile-settings-title",
    /** One segmented choice; + data-setting, data-value, aria-checked. */
    settingsOption: "mobile-settings-option",
    /** A language row; + data-value, aria-checked. */
    settingsLanguage: "mobile-settings-language",
    /** A read-only "Change on desktop" row. */
    settingsDesktopRow: "mobile-settings-desktop-row",
    /** The 5-second Undo after a change. */
    settingsUndo: "mobile-settings-undo",
    detailsSheet: "mobile-details-sheet",
    /** Check document: the sheet, its summary line, Check again. */
    checkSheet: "mobile-check-sheet",
    checkSummary: "mobile-check-summary",
    checkRerun: "mobile-check-rerun",
    /** One issue row; + data-severity (error | warning), data-block-id when it has a block. */
    checkIssue: "mobile-check-issue",
    /** The issue card over the page after a tap: Issue n of N ‹ ›, Ask. */
    issueCard: "mobile-issue-card",
    issuePrev: "mobile-issue-prev",
    issueNext: "mobile-issue-next",
    issueAsk: "mobile-issue-ask",
    issueClose: "mobile-issue-close",
    /** Preview's "See issues" when it couldn't rebuild. */
    previewIssues: "mobile-preview-issues",
    /** Preview's PDF · LaTeX switch; + data-mode (pdf | latex) on each button. */
    previewMode: "mobile-preview-mode",
    /** View LaTeX: the source, and its Copy. */
    latexView: "mobile-latex-view",
    latexCopy: "mobile-latex-copy",
    /** The second chip on a selected block: View LaTeX. */
    viewLatexChip: "mobile-view-latex-chip",
  },
  /** In-app notifications (Olivia, 10 Oct, sharing-notifications). */
  notifications: {
    bell: "notifications-bell",
    /** The unread count on the bell. */
    badge: "notifications-badge",
    panel: "notifications-panel",
    /** One row; + data-type, data-unread. */
    row: "notification-row",
    markAll: "notifications-mark-all",
    empty: "notifications-empty",
  },
  /** Ask Lilia in its own window (Olivia, 10 Oct, handoff-ask-lilia-window). */
  askWindow: {
    /** The header's Move Ask Lilia icon, and its two items. */
    move: "ask-lilia-move",
    /** Where Ask Lilia lives in the editor: half and half, or docked (Olivia's names). */
    sideBySide: "ask-move-side-by-side",
    dock: "ask-move-dock",
    openWindow: "ask-lilia-open-window",
    keepOnTop: "ask-lilia-keep-on-top",
    /** In the other window: Bring back to editor. */
    bringBack: "ask-lilia-bring-back",
    /** The editor's 32 px tab on the right edge while Ask Lilia is out; + data-working. */
    edgeTab: "ask-lilia-edge-tab",
    /** The tab's "Bring back", when the browser won't switch windows. */
    edgeBringBack: "ask-lilia-edge-bring-back",
    /** The page in the other window. */
    page: "ask-lilia-window-page",
  },
  tikzSplit: {
    root: "tikz-split",
    /** The source pane (CodeMirror inside). */
    source: "tikz-source",
    /** The drawing pane; its <img> carries data-dim (drawing | last-good). */
    drawing: "tikz-drawing",
    drawingImage: "tikz-drawing-image",
    /** The status chip: data-state = drawn | drawing | error | empty. */
    status: "tikz-status",
    /** The message under the failing line. */
    errorInline: "tikz-error-inline",
    /** An error with no line (timeout, budget, unreachable): in the drawing pane. */
    errorPanel: "tikz-error-panel",
    tryAgain: "tikz-try-again",
    splitter: "tikz-splitter",
    done: "tikz-done",
    caption: "tikz-caption",
    zoom: "tikz-zoom",
    /** The header's Templates button, and its popover. */
    templates: "tikz-templates",
    templatesPopover: "tikz-templates-popover",
    /** The empty state: the gallery in place of the panes. */
    gallery: "tikz-gallery",
    /** A template card (data-template = commutative | flowchart | plot | tree | graph | blank). */
    template: "tikz-template",
    describe: "tikz-describe",
    describeSend: "tikz-describe-send",
    replaceConfirm: "tikz-replace-confirm",
    replaceOk: "tikz-replace-ok",
    replaceCancel: "tikz-replace-cancel",
    /** "Added \usetikzlibrary{…} to the preamble." with Undo. */
    preambleNotice: "tikz-preamble-notice",
    preambleUndo: "tikz-preamble-undo",
    /** The lilia- colour list (each row data-colour-name). */
    colourAutocomplete: "tikz-colour-autocomplete",
    colourOption: "tikz-colour-option",
    /** < 760 px: the Source · Drawing tabs. */
    tabSource: "tikz-tab-source",
    tabDrawing: "tikz-tab-drawing",
  },
  refPicker: {
    root: "ref-picker",
    groupDocument: "ref-picker-group-document",
    groupLibrary: "ref-picker-group-library",
    row: "ref-picker-row",
  },

  /**
   * A table block in the Flow canvas and its edit dialog. Cells and header
   * cells repeat, with data-row / data-col.
   */
  /** Full width (both columns) in the table and figure edit windows (FullWidthSwitch). */
  fullWidth: "full-width-switch",
  table: {
    block: "table-block",
    headerCell: "table-header-cell",
    cell: "table-cell",
    edit: "table-edit",
    editCaption: "table-edit-caption",
    editHeader: "table-edit-header",
    editCell: "table-edit-cell",
    editAddRow: "table-edit-add-row",
    editAddColumn: "table-edit-add-column",
    editRemoveRow: "table-edit-remove-row",
    editRemoveColumn: "table-edit-remove-column",
  },
  tableTool: {
    /** Tool header — the table's identity across the top. */
    header: "tool-header",
    caption: "tool-caption",
    label: "tool-label",
    /** "Use in paper" — puts the table being edited into a paper. */
    useIn: "tool-use-in",
    more: "tool-more",

    /** Panes and their tab strips. `pane` is "left" | "right". */
    panes: "tool-panes",
    pane: (which: string) => `tool-pane-${which}`,
    paneTabs: (which: string) => `tool-pane-tabs-${which}`,
    paneTab: (which: string, surface: string) => `tool-pane-tab-${which}-${surface}`,

    /** Context bar — what the table IS, above the grid. */
    contextBar: "tool-context-bar",
    headerToggle: "tool-header-toggle",
    borderPreset: "tool-border-preset",
    cellBold: "tool-cell-bold",
    cellMath: "tool-cell-math",

    /** Surfaces. */
    source: "tool-source",
    sourceLatex: "tool-source-latex",
    sourceCopy: "tool-source-copy",
    sourceApply: "tool-source-apply",
    sourceRefresh: "tool-source-refresh",
    sourceProve: "tool-source-prove",
    sourceVerdict: "tool-source-verdict",
    sourceEngine: "tool-source-engine",
    sourceRevert: "tool-source-revert",
    sourceRefusal: "tool-source-refusal",
    /** Ask — the assistant, as a pane surface rather than a modal. */
    ask: "tool-ask",
    askInput: "tool-ask-input",
    askSend: "tool-ask-send",
    askSuggestion: "tool-ask-suggestion",
    askTurn: "tool-ask-turn",
    askApply: "tool-ask-apply",
    askDiscard: "tool-ask-discard",
    askVerdict: "tool-ask-verdict",
    /** An answer that changed nothing: said so, nothing to apply. */
    askUnchanged: "tool-ask-unchanged",
    askError: "tool-ask-error",

    preview: "tool-preview",
    previewCaption: "tool-preview-caption",

    /** Rail and panels. */
    rail: "tool-rail",
    railTab: (panel: string) => `tool-panel-${panel}`,
    panel: "tool-panel",
    panelClose: "tool-panel-close",
    workspace: "tool-workspace",

    /**
     * The grid's shared column menu (the ⋯ on each column). The 2g items —
     * decimal alignment, the unit offer, bold best — live in it; bold best's two
     * items share a testid and say which with `data-best` (higher | lower), the
     * unit item carries `data-unit`.
     */
    colMenuOpen: "table-col-menu-open",
    colMenu: "table-col-menu",
    colAlign: (a: string) => `table-col-align-${a}`,
    colAlignDecimal: "table-col-align-decimal",
    colUnit: "table-col-unit",
    colBoldBest: "table-col-bold-best",
    /** The unit a column carries, shown in its grid header chrome. */
    colUnitBadge: "table-col-unit-badge",
    /** A number in a decimal-aligned column, split at its point (previews, canvas). */
    decimalNumber: "table-decimal-number",
    /**
     * The rest of 2g (Olivia's README): group headers, notes, row groups. The
     * three menu items are her testids; the rest is what they open and show.
     * `table-group-columns` is in the cell menu of a header cell (with the
     * header cells selected by shift-click), `table-cell-note` in every cell's
     * menu, `table-row-group` in the row menu.
     */
    groupColumns: "table-group-columns",
    ungroupColumns: "table-ungroup-columns",
    cellNote: "table-cell-note",
    cellNoteRemove: "table-cell-note-remove",
    rowGroup: "table-row-group",
    /** The row menu (the ⋯ in each body row's gutter). */
    rowMenuOpen: "table-row-menu-open",
    rowMenu: "table-row-menu",
    /** The cell menu: its ⋯ on the focused cell (or a right-click on any cell). */
    cellMenuOpen: "table-cell-menu-open",
    cellMenu: "table-cell-menu",
    /** The note editor the cell menu opens, its field and buttons. */
    noteEditor: "table-note-editor",
    noteInput: "table-note-input",
    noteDone: "table-note-done",
    /** A cell's note mark (data-mark), in the grid and every renderer. */
    noteMark: "table-note-mark",
    /** The notes under a table (grid, previews, canvas); each note carries data-mark. */
    notes: "table-notes",
    note: "table-note",
    /** The group header row and one group's label (data-start), grid and renderers. */
    groupRow: "table-group-row",
    groupLabel: "table-group-label",

    /** Columns panel. */
    columnRow: "tool-column-row",
    columnAlign: (a: string) => `tool-column-align-${a}`,
    columnNumeric: "tool-column-numeric",
    addColumn: "tool-add-column",
    deleteColumn: "tool-delete-column",

    /** Problems panel. */
    problemRow: "tool-problem-row",
    railVerdict: "tool-rail-verdict",
    railCheck: "tool-rail-check",
    railEngine: "tool-rail-engine",

    /** The grid pane's empty state — SPEC.md §4a. */
    startSurface: "tool-start-surface",
    startMesh: "tool-start-mesh",
    startSizeField: "tool-start-size-field",
    startCreate: "tool-start-create",
    startCsv: "tool-start-csv",
    startPasteHint: "tool-start-paste-hint",
    startAsk: "tool-start-ask",
    saveState: "tool-save-state",

    /** The Tables listing — SPEC.md §4a. */
    listing: "tables-listing",
    listingEmpty: "tables-listing-empty",
    listingNew: "tables-listing-new",
    listingSearch: "tables-listing-search",
    listingNewTile: "tables-listing-new-tile",
    card: "table-card",
    cardOpen: "table-card-open",
    cardMenu: "table-card-menu",
    cardUsage: "table-card-usage",
    cardUseIn: "table-card-use-in",
    /** Olivia's name for the card's Use in paper; wraps `cardUseIn`. */
    cardUse: "table-card-use",
    cardMenuDelete: "table-card-menu-delete",
    cardSkeleton: "table-card-skeleton",
    /**
     * The tables page is Split (Olivia, tables-modern turn 3): the list column
     * and the read-only detail of the selected table. No view switcher.
     */
    splitList: "tables-split-list",
    /** A row; + data-table-id, aria-selected. */
    splitRow: "tables-split-row",
    splitRowProblems: "tables-split-row-problems",
    splitDetail: "tables-split-detail",
    splitName: "tables-split-name",
    splitRename: "tables-split-rename",
    splitFacts: "tables-split-facts",
    splitOpen: "tables-split-open",
    splitUse: "tables-split-use",
    splitMore: "tables-split-more",
    splitMenuRename: "tables-split-menu-rename",
    splitMenuDuplicate: "tables-split-menu-duplicate",
    splitMenuDelete: "tables-split-menu-delete",
    /** Below 1100 px, one pane at a time: "‹ All tables". */
    splitBack: "tables-split-back",
    splitUsedIn: "tables-split-used-in",
    splitUsedInRow: "tables-split-used-in-row",
    splitUsedInOpen: "tables-split-used-in-open",
    splitCopies: "tables-split-copies",
    splitCopyRow: "tables-split-copy-row",
    splitCopyShow: "tables-split-copy-show",
    splitProblems: "tables-split-problems",
    splitNoMatch: "tables-split-no-match",
    splitKeys: "tables-split-keys",
    listingSort: "tables-listing-sort",
    listingSortOption: "tables-listing-sort-option",
    listingCount: "tables-listing-count",

    /** Link-or-copy at insert — SPEC.md §4a. */
    insertDialog: "table-insert-dialog",
    insertSearch: "table-insert-search",
    insertRow: "table-insert-row",
    insertLink: "table-insert-link",
    insertCopy: "table-insert-copy",
    /** The insert sheet — design-handoffs/2026-09-27-tables-modern (1a, 1b). */
    insertSheet: "table-insert-sheet",
    paperRow: "paper-insert-row",
    paperSection: "paper-insert-section",
    insertLanding: "table-insert-landing",
    insertLandingChange: "table-insert-landing-change",
    insertChoiceLink: "table-insert-choice-link",
    insertChoiceCopy: "table-insert-choice-copy",
    insertCommit: "table-insert-commit",
    insertCommitStay: "table-insert-commit-stay",
    insertUsedIn: "table-insert-used-in",
    insertNewTable: "table-insert-new-table",
    insertEmpty: "table-insert-empty",
    insertToast: "table-insert-toast",
    insertToastOpen: "table-insert-toast-open",
    insertClose: "table-insert-close",
    /** The shared typeset, tile-clipped preview. */
    tablePreview: "table-preview",
    /** Its caption, under the whole table (`clip="none"`). */
    tablePreviewCaption: "table-preview-caption",
    /** Preview as: Plain · each paper the table is in (document themes, 2a). */
    previewAs: "table-preview-as",
    /** One option (+ `data-doc-id` for a paper, `data-value="plain"` for Plain). */
    previewAsOption: "table-preview-as-option",
    /** The tool header's Used in chip and its popover (1e). */
    usedIn: "table-tool-used-in",
    usedInPanel: "table-tool-used-in-panel",
    usedInRow: "table-tool-used-in-row",
    usedInOpen: "table-tool-used-in-open",
    usedInFooter: "table-tool-used-in-footer",
    /** Olivia's name for the header's Use in paper; wraps `useIn` (tool-use-in). */
    useInPaper: "table-tool-use-in-paper",
    upsell: "table-use-in-upsell",
    upsellDismiss: "table-use-in-upsell-dismiss",
    upsellUpgrade: "table-use-in-upsell-upgrade",
    backToListing: "tool-back-to-listing",

    /** The span guard — read-only while merged cells cannot be represented. */
    spanBanner: "table-spans-blocked",
  },
  /** /import — the Import tab's paste mode, and the Export tab. */
  importExport: {
    /** Paste mode's text; its format is a radio, data-format="lilia" | "latex". */
    pasteText: "import-paste-text",
    pasteFormat: "import-paste-format",
    importError: "import-error",
    /** One format card, data-format="PDF" | "DOCX" | "LATEX" | "MARKDOWN" | "HTML" | "LML". */
    exportFormat: "export-format",
    /** An export option, data-option (e.g. "includeImages"). */
    exportOption: "export-option",
    exportSubmit: "export-submit",
    exportError: "export-error",
  },
  /** /export/latex/:documentId — the LaTeX code view and project download. */
  latexExport: {
    page: "latex-export-page",
    back: "latex-export-back",
    download: "latex-export-download",
    copy: "latex-export-copy",
    preview: "latex-export-preview",
    /** A select's trigger or a switch, data-option (e.g. "documentClass", "includeImages"). */
    option: "latex-export-option",
    /** An item in an open select, data-value. */
    choice: "latex-export-choice",
    /** A publisher document's submission panel (data-status: passed, warnings, errors). */
    submission: "latex-export-submission",
    /** One compliance finding, data-severity and data-code. */
    finding: "latex-export-finding",
    guidelines: "latex-export-guidelines",
    /** Download a submission the check found errors in: the question, and its two answers. */
    confirm: "latex-export-confirm",
    confirmCancel: "latex-export-confirm-cancel",
    confirmDownload: "latex-export-confirm-download",
    error: "latex-export-error",
    /** IEEE's PDF check: the panel (data-status) and its button. */
    pdfCheck: "latex-export-pdf-check",
    pdfCheckRun: "latex-export-pdf-check-run",
    /** ACM's TAPS ZIP name: the proceeding acronym and the paper id. */
    /** Open in Overleaf (25 Oct 2026). */
    overleaf: "latex-export-overleaf",
    tapsAcronym: "latex-export-taps-acronym",
    tapsPaperId: "latex-export-taps-paper-id",
    /** Kept submissions (data-count), the changes since the latest (data-added, data-removed), their actions. */
    submissions: "latex-export-submissions",
    changes: "latex-export-changes",
    changesShow: "latex-export-changes-show",
    markedUp: "latex-export-marked-up",
    diff: "latex-export-diff",
  },
  /** /d/:id/print — the print view. */
  print: {
    page: "print-page",
    content: "print-content",
    print: "print-button",
    error: "print-error",
  },
  /** /import-summary/:sessionId — the one-screen summary before importing. */
  importSummary: {
    page: "import-summary",
    back: "import-summary-back",
    /** One block-type count, data-type and data-count. */
    count: "import-summary-count",
    /** data-errors, data-warnings. */
    quality: "import-summary-quality",
    coverage: "import-summary-coverage",
    review: "import-summary-review",
    import: "import-summary-import",
    error: "import-summary-error",
  },
  /** /import-review/:sessionId/report — the end-of-review report. */
  importReport: {
    page: "import-report",
    title: "import-report-title",
    /** data-status. */
    status: "import-report-status",
    /** One number, data-kpi and data-value. */
    kpi: "import-report-kpi",
    openDocument: "import-report-open-document",
    /** A row of the unsupported-features table, data-token. */
    unsupported: "import-report-unsupported",
  },
  /** /import-review — every import the author started. */
  importReviewList: {
    page: "import-review-list",
    /** data-scope="active" | "history" | "all". */
    scope: "import-review-scope",
    formatFilter: "import-review-format-filter",
    /** One import, data-session-id and data-status. */
    item: "import-review-item",
    empty: "import-review-empty",
  },
  /** /import-review/tex/:sessionId — the aspect tabs and the Diagnostics tab. */
  importReview: {
    /** data-aspect, data-active. */
    aspectTab: "import-review-aspect-tab",
    /** data-count: diagnostics not dismissed. */
    diagnostics: "import-review-diagnostics",
    /** One diagnostic, data-severity and data-code. */
    diagnostic: "import-review-diagnostic",
    diagnosticsClean: "import-review-diagnostics-clean",
    /** The bulk-convert bar: data-action on each action, data-level on each heading level. */
    bulkBar: "review-bulk-convert-bar",
    bulkAction: "review-bulk-convert",
    bulkLevel: "review-bulk-convert-level",
    bulkClear: "review-bulk-convert-clear",
    /** The kanban layout: a lane (data-status) and a card (data-block-id, data-block-type). */
    kanbanLane: "review-kanban-lane",
    kanbanCard: "review-kanban-card",
    kanbanSelect: "review-kanban-select",
    /** A card's lane mover on small screens, data-status of the lane it moves to. */
    kanbanMove: "review-kanban-move",
    /** The pill on a card whose block was edited (by hand or by bulk convert). */
    kanbanEdited: "review-kanban-edited",
  },
  /** "Sign in as…" on the test copy (lib/testActAs); data-person on each choice. */
  testActAs: {
    root: "test-act-as",
    toggle: "test-act-as-toggle",
    person: "test-act-as-person",
    mail: "test-act-as-mail",
    /** "Other…": a name or an address, and its Go. */
    otherInput: "test-act-as-other",
    otherGo: "test-act-as-other-go",
  },
} as const;

/** Build a `[data-testid="…"]` CSS selector. */
export const byTestId = (id: string): string => `[data-testid="${id}"]`;
